import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play } from "lucide-react";
import { VIDEOS } from "../../lib/siteData";

const VideoTile = ({ clip, className = "", delay = 0 }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.paused) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden clip-frame border border-[#223026] bg-black ${className}`}
      data-testid={`video-tile-${clip.orientation}`}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={clip.src}
        poster={clip.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video"
      />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/55 via-transparent to-black/20" />

      <div className="absolute top-4 left-4 z-10">
        <span className="inline-flex items-center gap-1.5 bg-lime-400 text-black text-[11px] uppercase tracking-[0.15em] font-bold px-3 py-1.5">
          <Play size={11} /> {clip.label}
        </span>
      </div>

      <button
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        data-testid="video-mute-toggle"
        className="absolute bottom-4 right-4 z-10 w-11 h-11 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-[#2A3A2E] text-white hover:bg-lime-400 hover:text-black transition-colors duration-200"
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
    </motion.div>
  );
};

export const VideoSection = () => {
  const landscape = VIDEOS.find((v) => v.orientation === "landscape");
  const portrait = VIDEOS.find((v) => v.orientation === "portrait");

  return (
    <section
      id="video"
      className="relative bg-[#080D0A] py-28 md:py-40 border-t border-[#223026]"
      data-testid="video-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] text-lime-400 font-bold">
              In Motion
            </span>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-6xl text-white mt-4 leading-none">
              See the work <br /> in motion.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-sm text-base md:text-lg">
            Straight from the seat — real footage of Eklund Land Management out on
            the job.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {landscape && <VideoTile clip={landscape} className="lg:col-span-2 aspect-video" />}
          {portrait && (
            <VideoTile
              clip={portrait}
              delay={0.1}
              className="lg:col-span-1 aspect-[9/16] max-h-[560px] mx-auto w-full"
            />
          )}
        </div>
      </div>
    </section>
  );
};
